/**
 * Tipos para la estructura de error del backend
 */
export interface BackendError {
  statusCode: number;
  timestamp: string;
  path: string;
  message: string;
  errorCode: string;
  error: string;
  traceId: string;
}

/**
 * Enum centralizado de códigos de error semánticos y user-friendly
 * Elimina duplicación y usa nombres descriptivos en lugar de códigos numéricos
 */
export enum AppErrorCode {
  // Validación General
  "REQUIRED_FIELD" = "REQUIRED_FIELD",
  "INVALID_INPUT" = "INVALID_INPUT",
  "INVALID_EMAIL" = "INVALID_EMAIL",
  "INVALID_PHONE" = "INVALID_PHONE",
  "INVALID_DATE" = "INVALID_DATE",
  "INVALID_FORMAT" = "INVALID_FORMAT",
  "INVALID_RANGE" = "INVALID_RANGE",

  // Operaciones CRUD Generales
  "ITEM_NOT_FOUND" = "ITEM_NOT_FOUND",
  "DUPLICATE_ITEM" = "DUPLICATE_ITEM",
  "SAVE_ERROR" = "SAVE_ERROR",
  "DELETE_ERROR" = "DELETE_ERROR",
  "UPDATE_ERROR" = "UPDATE_ERROR",
  "DUPLICATE_EMAIL" = "DUPLICATE_EMAIL",

  // Estados y Transiciones
  "INVALID_STATE" = "INVALID_STATE",
  "INVALID_STATE_TRANSITION" = "INVALID_STATE_TRANSITION",
  "OPERATION_NOT_ALLOWED" = "OPERATION_NOT_ALLOWED",

  // Autenticación y Autorización
  "INVALID_CREDENTIALS" = "INVALID_CREDENTIALS",
  "INVALID_PASSWORD" = "INVALID_PASSWORD",
  "TOKEN_EXPIRED" = "TOKEN_EXPIRED",
  "TOKEN_INVALID" = "TOKEN_INVALID",
  "INSUFFICIENT_PERMISSIONS" = "INSUFFICIENT_PERMISSIONS",
  "ACCESS_DENIED" = "ACCESS_DENIED",
  "ACCOUNT_LOCKED" = "ACCOUNT_LOCKED",
  "PASSWORD_REQUIREMENTS_NOT_MET" = "PASSWORD_REQUIREMENTS_NOT_MET",

  // Negocio - Órdenes
  "ORDER_NOT_FOUND" = "ORDER_NOT_FOUND",
  "ORDER_ALREADY_CONFIRMED" = "ORDER_ALREADY_CONFIRMED",
  "ORDER_CANNOT_CANCEL" = "ORDER_CANNOT_CANCEL",
  "ORDER_INVALID_STATUS" = "ORDER_INVALID_STATUS",
  "ORDER_EMPTY_CART" = "ORDER_EMPTY_CART",
  "ORDER_ITEM_ERROR" = "ORDER_ITEM_ERROR",
  "ORDER_PAYMENT_REQUIRED" = "ORDER_PAYMENT_REQUIRED",
  /** La misma `Idempotency-Key` con otro cuerpo (trd-core#1179). */
  "IDEMPOTENCY_KEY_REUSED" = "IDEMPOTENCY_KEY_REUSED",

  // Inventario
  "INSUFFICIENT_STOCK" = "INSUFFICIENT_STOCK",
  "PRODUCT_NOT_FOUND" = "PRODUCT_NOT_FOUND",
  "VARIANT_NOT_FOUND" = "VARIANT_NOT_FOUND",
  "WAREHOUSE_NOT_FOUND" = "WAREHOUSE_NOT_FOUND",
  "INVALID_QUANTITY" = "INVALID_QUANTITY",
  "STOCK_RESERVED" = "STOCK_RESERVED",
  "PRODUCT_DISCONTINUED" = "PRODUCT_DISCONTINUED",

  // Catálogo
  "CATEGORY_NOT_FOUND" = "CATEGORY_NOT_FOUND",
  "BRAND_NOT_FOUND" = "BRAND_NOT_FOUND",
  "SUPPLIER_NOT_FOUND" = "SUPPLIER_NOT_FOUND",
  "BARCODE_GENERATION_FAILED" = "BARCODE_GENERATION_FAILED",
  "BARCODE_ALREADY_EXISTS" = "BARCODE_ALREADY_EXISTS",

  // Precios
  "PRICE_NOT_FOUND" = "PRICE_NOT_FOUND",
  "INVALID_PRICE" = "INVALID_PRICE",
  "PRICE_PLAN_NOT_FOUND" = "PRICE_PLAN_NOT_FOUND",
  "DISCOUNT_NOT_APPLICABLE" = "DISCOUNT_NOT_APPLICABLE",
  "PRICE_CALCULATION_ERROR" = "PRICE_CALCULATION_ERROR",

  // Pagos y Cobranzas
  "INSUFFICIENT_FUNDS" = "INSUFFICIENT_FUNDS",
  "PAYMENT_METHOD_NOT_AVAILABLE" = "PAYMENT_METHOD_NOT_AVAILABLE",
  "PAYMENT_PROCESSING_ERROR" = "PAYMENT_PROCESSING_ERROR",
  "INVALID_PAYMENT_AMOUNT" = "INVALID_PAYMENT_AMOUNT",
  "PAYMENT_ALREADY_PROCESSED" = "PAYMENT_ALREADY_PROCESSED",
  "REFUND_NOT_ALLOWED" = "REFUND_NOT_ALLOWED",

  // Tesorería
  "CASH_DESK_CLOSED" = "CASH_DESK_CLOSED",
  "CASH_DESK_NOT_FOUND" = "CASH_DESK_NOT_FOUND",
  "INSUFFICIENT_CASH_BALANCE" = "INSUFFICIENT_CASH_BALANCE",
  "INVALID_CASH_OPERATION" = "INVALID_CASH_OPERATION",
  "CHECK_INVALID" = "CHECK_INVALID",
  "CHECK_ALREADY_DEPOSITED" = "CHECK_ALREADY_DEPOSITED",

  // Personas (Buyers, Employees, Owners)
  "BUYER_NOT_FOUND" = "BUYER_NOT_FOUND",
  "EMPLOYEE_NOT_FOUND" = "EMPLOYEE_NOT_FOUND",
  "OWNER_NOT_FOUND" = "OWNER_NOT_FOUND",
  "PERSON_ALREADY_EXISTS" = "PERSON_ALREADY_EXISTS",

  // Cupones y Ofertas
  "COUPON_NOT_FOUND" = "COUPON_NOT_FOUND",
  "COUPON_EXPIRED" = "COUPON_EXPIRED",
  "COUPON_ALREADY_USED" = "COUPON_ALREADY_USED",
  "COUPON_NOT_APPLICABLE" = "COUPON_NOT_APPLICABLE",
  "OFFER_NOT_FOUND" = "OFFER_NOT_FOUND",
  "OFFER_EXPIRED" = "OFFER_EXPIRED",

  // Reservas
  "RESERVATION_NOT_FOUND" = "RESERVATION_NOT_FOUND",
  "RESERVATION_CONFLICT" = "RESERVATION_CONFLICT",
  "RESERVABLE_ITEM_NOT_FOUND" = "RESERVABLE_ITEM_NOT_FOUND",
  "BOOKING_NOT_FOUND" = "BOOKING_NOT_FOUND",
  "SERVICE_NOT_AVAILABLE" = "SERVICE_NOT_AVAILABLE",

  // Compras
  "PURCHASE_NOT_FOUND" = "PURCHASE_NOT_FOUND",
  "STOCK_ORDER_NOT_FOUND" = "STOCK_ORDER_NOT_FOUND",
  "SUPPLIER_SERVICE_ERROR" = "SUPPLIER_SERVICE_ERROR",

  // Facturación
  "INVOICE_NOT_FOUND" = "INVOICE_NOT_FOUND",
  "INVOICE_ALREADY_ISSUED" = "INVOICE_ALREADY_ISSUED",
  "AFIP_SERVICE_ERROR" = "AFIP_SERVICE_ERROR",
  "INVOICE_GENERATION_ERROR" = "INVOICE_GENERATION_ERROR",

  // Desembolsos
  "DISBURSEMENT_NOT_FOUND" = "DISBURSEMENT_NOT_FOUND",
  "PAYABLE_NOT_FOUND" = "PAYABLE_NOT_FOUND",

  // Mensajería
  "MESSAGE_SEND_FAILED" = "MESSAGE_SEND_FAILED",
  "SUBSCRIBER_NOT_FOUND" = "SUBSCRIBER_NOT_FOUND",
  "NOTIFICATION_SERVICE_ERROR" = "NOTIFICATION_SERVICE_ERROR",

  // Archivos
  "FILE_UPLOAD_FAILED" = "FILE_UPLOAD_FAILED",
  "FILE_NOT_FOUND" = "FILE_NOT_FOUND",
  "FILE_TOO_LARGE" = "FILE_TOO_LARGE",
  "FILE_INVALID_FORMAT" = "FILE_INVALID_FORMAT",
  "FILE_CORRUPTED" = "FILE_CORRUPTED",

  // Servicios Externos
  "EXTERNAL_SERVICE_ERROR" = "EXTERNAL_SERVICE_ERROR",
  "EXTERNAL_SERVICE_TIMEOUT" = "EXTERNAL_SERVICE_TIMEOUT",
  "EXTERNAL_SERVICE_UNAVAILABLE" = "EXTERNAL_SERVICE_UNAVAILABLE",
  "API_RATE_LIMIT_EXCEEDED" = "API_RATE_LIMIT_EXCEEDED",

  // Sistema
  "DATABASE_ERROR" = "DATABASE_ERROR",
  "DATABASE_CONNECTION_ERROR" = "DATABASE_CONNECTION_ERROR",
  "CONFIGURATION_ERROR" = "CONFIGURATION_ERROR",
  "INTERNAL_SERVER_ERROR" = "INTERNAL_SERVER_ERROR",
  "TIMEOUT_ERROR" = "TIMEOUT_ERROR",
  "UNKNOWN_ERROR" = "UNKNOWN_ERROR",

  // Red y Conectividad
  "NETWORK_ERROR" = "NETWORK_ERROR",
  "CONNECTION_TIMEOUT" = "CONNECTION_TIMEOUT",
  "SERVICE_UNAVAILABLE" = "SERVICE_UNAVAILABLE",

  // Concurrencia
  "RESOURCE_LOCKED" = "RESOURCE_LOCKED",
  "CONCURRENT_MODIFICATION" = "CONCURRENT_MODIFICATION",
  "TRANSACTION_FAILED" = "TRANSACTION_FAILED",

  // Invitación a un email que ya tiene usuario. identity lo detecta y responde
  // 409, pero **sin body**: `{"statusCode":409}`, sin código ni mensaje. Por eso
  // el front tiene que derivar el código del status HTTP — si no, `ErrorMapper`
  // cae en el default y la pantalla dice "Error código 3312" sobre un problema
  // que se arregla cambiando una letra del email.
  "USER_EMAIL_ALREADY_INVITED" = "USER_EMAIL_ALREADY_INVITED",

  // Alta a medias: el orquestador de invitación del BFF creó al usuario en
  // identity (y mandó el mail) pero NO pudo materializar el empleado en core,
  // así que responde 201 con `operational.persisted:false`. No es un fallo de
  // red ni una validación: el alta quedó por la mitad y reintentarla duplica
  // la invitación sin arreglar nada.
  "OPERATIONAL_NOT_PERSISTED" = "OPERATIONAL_NOT_PERSISTED",

  // Códigos de identity v1.20.0 (trd-identity#136) que el BFF pasa con sus
  // `details` desde trd-bff#339 (trd-client#342). Van en snake_case, tal cual
  // los manda identity.
  //
  // Re-invitar a alguien que ya es miembro: `409` con
  // `details: { user_id, is_active }`.
  "MEMBER_ALREADY_EXISTS" = "member_already_exists",
  // Dar un permiso que el negocio no tiene habilitado (o un id con un typo):
  // `422` con `details.resources`.
  "GRANT_RESOURCE_NOT_ENABLED" = "grant_resource_not_enabled",
}

/**
 * Mapeo de códigos de error a mensajes en español user-friendly
 */
const ERROR_MESSAGES: Record<AppErrorCode, string> = {
  // Validación General
  [AppErrorCode.REQUIRED_FIELD]: "Este campo es obligatorio",
  [AppErrorCode.INVALID_INPUT]: "Los datos ingresados no son válidos",
  [AppErrorCode.INVALID_EMAIL]: "Por favor ingresá un email válido",
  [AppErrorCode.INVALID_PHONE]: "El teléfono debe tener un formato válido",
  [AppErrorCode.INVALID_DATE]: "La fecha seleccionada no es válida",
  [AppErrorCode.INVALID_FORMAT]: "El formato ingresado es incorrecto",
  [AppErrorCode.INVALID_RANGE]:
    "El valor debe estar entre los límites permitidos",

  // Operaciones CRUD Generales
  [AppErrorCode.ITEM_NOT_FOUND]: "No pudimos encontrar lo que buscás",
  [AppErrorCode.DUPLICATE_ITEM]: "Ya existe un registro con estos datos",
  [AppErrorCode.SAVE_ERROR]: "No pudimos guardar los cambios.",
  [AppErrorCode.DELETE_ERROR]:
    "No se pudo eliminar el registro. Verificá que no esté en uso",
  [AppErrorCode.UPDATE_ERROR]:
    "Hubo un problema al actualizar. Por favor intentá de nuevo",
  [AppErrorCode.DUPLICATE_EMAIL]: "Ya existe una cuenta con este email",

  // Estados y Transiciones
  [AppErrorCode.INVALID_STATE]:
    "Esta operación no se puede realizar en el estado actual",
  [AppErrorCode.INVALID_STATE_TRANSITION]:
    "No es posible cambiar a ese estado desde aquí",
  [AppErrorCode.OPERATION_NOT_ALLOWED]:
    "No tenés permisos para realizar esta acción",

  // Autenticación y Autorización
  [AppErrorCode.INVALID_CREDENTIALS]: "Usuario o contraseña incorrectos",
  [AppErrorCode.INVALID_PASSWORD]: "La contraseña ingresada es incorrecta",
  [AppErrorCode.TOKEN_EXPIRED]:
    "Tu sesión expiró. Por favor, volvé a iniciar sesión",
  [AppErrorCode.TOKEN_INVALID]:
    "Sesión inválida. Por favor, iniciá sesión nuevamente",
  [AppErrorCode.INSUFFICIENT_PERMISSIONS]:
    "No tenés los permisos necesarios para esta acción",
  [AppErrorCode.ACCESS_DENIED]: "No podés acceder a esta sección",
  [AppErrorCode.ACCOUNT_LOCKED]:
    "Tu cuenta está bloqueada. Contactá al administrador",
  [AppErrorCode.PASSWORD_REQUIREMENTS_NOT_MET]:
    "La contraseña debe tener al menos 8 caracteres, una mayúscula y un número",

  // Negocio - Órdenes
  [AppErrorCode.ORDER_NOT_FOUND]: "No encontramos la orden que buscás",
  [AppErrorCode.ORDER_ALREADY_CONFIRMED]:
    "Esta orden ya fue confirmada y no se puede modificar",
  [AppErrorCode.ORDER_CANNOT_CANCEL]: "Esta orden ya no se puede cancelar",
  [AppErrorCode.ORDER_INVALID_STATUS]:
    "La orden está en un estado que no permite esta acción",
  [AppErrorCode.ORDER_EMPTY_CART]: "Agregá productos antes de continuar",
  [AppErrorCode.ORDER_ITEM_ERROR]:
    "Hay un problema con algunos productos de la orden",
  [AppErrorCode.ORDER_PAYMENT_REQUIRED]:
    "Debés completar el pago para procesar la orden",
  [AppErrorCode.IDEMPOTENCY_KEY_REUSED]:
    "Esta venta ya figura registrada con otro contenido. Revisá las órdenes antes de volver a cobrar",

  // Inventario
  [AppErrorCode.INSUFFICIENT_STOCK]: "No hay suficiente stock disponible",
  [AppErrorCode.PRODUCT_NOT_FOUND]: "No encontramos el producto que buscás",
  [AppErrorCode.VARIANT_NOT_FOUND]: "No existe esa variante del producto",
  [AppErrorCode.WAREHOUSE_NOT_FOUND]: "El depósito seleccionado no existe",
  [AppErrorCode.INVALID_QUANTITY]: "La cantidad ingresada no es válida",
  [AppErrorCode.STOCK_RESERVED]:
    "Este stock está reservado para otra operación",
  [AppErrorCode.PRODUCT_DISCONTINUED]: "Este producto ya no está disponible",

  // Catálogo
  [AppErrorCode.CATEGORY_NOT_FOUND]: "No encontramos esa categoría",
  [AppErrorCode.BRAND_NOT_FOUND]: "La marca seleccionada no existe",
  [AppErrorCode.SUPPLIER_NOT_FOUND]: "No encontramos ese proveedor",
  [AppErrorCode.BARCODE_GENERATION_FAILED]:
    "No pudimos generar el código de barras. Intentá nuevamente",
  [AppErrorCode.BARCODE_ALREADY_EXISTS]:
    "Ya existe un producto con ese código de barras",

  // Precios
  [AppErrorCode.PRICE_NOT_FOUND]: "No hay precio disponible para este producto",
  [AppErrorCode.INVALID_PRICE]: "El precio ingresado no es válido",
  [AppErrorCode.PRICE_PLAN_NOT_FOUND]: "No encontramos la lista de precios",
  [AppErrorCode.DISCOUNT_NOT_APPLICABLE]: "Este descuento no se puede aplicar",
  [AppErrorCode.PRICE_CALCULATION_ERROR]:
    "Hubo un error al calcular el precio. Verificá los valores",

  // Pagos y Cobranzas
  [AppErrorCode.INSUFFICIENT_FUNDS]: "El saldo disponible no es suficiente",
  [AppErrorCode.PAYMENT_METHOD_NOT_AVAILABLE]:
    "Este método de pago no está disponible",
  [AppErrorCode.PAYMENT_PROCESSING_ERROR]:
    "No pudimos procesar el pago. Intentá nuevamente",
  [AppErrorCode.INVALID_PAYMENT_AMOUNT]: "El monto ingresado no es correcto",
  [AppErrorCode.PAYMENT_ALREADY_PROCESSED]:
    "Este pago ya fue procesado anteriormente",
  [AppErrorCode.REFUND_NOT_ALLOWED]: "No se puede realizar el reembolso",

  // Tesorería
  [AppErrorCode.CASH_DESK_CLOSED]:
    "La caja está cerrada. Debés abrirla para continuar",
  [AppErrorCode.CASH_DESK_NOT_FOUND]: "No encontramos la caja seleccionada",
  [AppErrorCode.INSUFFICIENT_CASH_BALANCE]:
    "No hay suficiente efectivo en caja para esta operación",
  [AppErrorCode.INVALID_CASH_OPERATION]: "Esta operación de caja no es válida",
  [AppErrorCode.CHECK_INVALID]: "Los datos del cheque no son válidos",
  [AppErrorCode.CHECK_ALREADY_DEPOSITED]: "Este cheque ya fue depositado",

  // Personas (Buyers, Employees, Owners)
  [AppErrorCode.BUYER_NOT_FOUND]: "No encontramos ese cliente",
  [AppErrorCode.EMPLOYEE_NOT_FOUND]: "No encontramos ese empleado",
  [AppErrorCode.OWNER_NOT_FOUND]: "No encontramos ese propietario",
  [AppErrorCode.PERSON_ALREADY_EXISTS]: "Ya existe una persona con esos datos",

  // Cupones y Ofertas
  [AppErrorCode.COUPON_NOT_FOUND]: "Cupón no encontrado",
  [AppErrorCode.COUPON_EXPIRED]: "Cupón expirado",
  [AppErrorCode.COUPON_ALREADY_USED]: "Cupón ya utilizado",
  [AppErrorCode.COUPON_NOT_APPLICABLE]: "Cupón no aplicable",
  [AppErrorCode.OFFER_NOT_FOUND]: "Oferta no encontrada",
  [AppErrorCode.OFFER_EXPIRED]: "Oferta expirada",

  // Reservas
  [AppErrorCode.RESERVATION_NOT_FOUND]: "Reserva no encontrada",
  [AppErrorCode.RESERVATION_CONFLICT]: "Conflicto de reserva",
  [AppErrorCode.RESERVABLE_ITEM_NOT_FOUND]: "Artículo reservable no encontrado",
  [AppErrorCode.BOOKING_NOT_FOUND]: "Reserva no encontrada",
  [AppErrorCode.SERVICE_NOT_AVAILABLE]: "Servicio no disponible",

  // Compras
  [AppErrorCode.PURCHASE_NOT_FOUND]: "Compra no encontrada",
  [AppErrorCode.STOCK_ORDER_NOT_FOUND]: "Orden de stock no encontrada",
  [AppErrorCode.SUPPLIER_SERVICE_ERROR]: "Error en el servicio del proveedor",

  // Facturación
  [AppErrorCode.INVOICE_NOT_FOUND]: "Factura no encontrada",
  [AppErrorCode.INVOICE_ALREADY_ISSUED]: "Factura ya emitida",
  [AppErrorCode.AFIP_SERVICE_ERROR]: "Error en el servicio de AFIP",
  [AppErrorCode.INVOICE_GENERATION_ERROR]: "Error al generar la factura",

  // Desembolsos
  [AppErrorCode.DISBURSEMENT_NOT_FOUND]: "Desembolso no encontrado",
  [AppErrorCode.PAYABLE_NOT_FOUND]: "Cuenta por pagar no encontrada",

  // Mensajería
  [AppErrorCode.MESSAGE_SEND_FAILED]: "Error al enviar mensaje",
  [AppErrorCode.SUBSCRIBER_NOT_FOUND]: "Suscriptor no encontrado",
  [AppErrorCode.NOTIFICATION_SERVICE_ERROR]:
    "Error en el servicio de notificaciones",

  // Archivos
  [AppErrorCode.FILE_UPLOAD_FAILED]: "Error al subir archivo",
  [AppErrorCode.FILE_NOT_FOUND]: "Archivo no encontrado",
  [AppErrorCode.FILE_TOO_LARGE]: "Archivo demasiado grande",
  [AppErrorCode.FILE_INVALID_FORMAT]: "Formato de archivo inválido",
  [AppErrorCode.FILE_CORRUPTED]: "Archivo corrupto",

  // Servicios Externos
  [AppErrorCode.EXTERNAL_SERVICE_ERROR]: "Error en servicio externo",
  [AppErrorCode.EXTERNAL_SERVICE_TIMEOUT]:
    "Tiempo de espera agotado en servicio externo",
  [AppErrorCode.EXTERNAL_SERVICE_UNAVAILABLE]: "Servicio externo no disponible",
  [AppErrorCode.API_RATE_LIMIT_EXCEEDED]: "Límite de solicitudes excedido",

  // Sistema
  [AppErrorCode.DATABASE_ERROR]: "Hubo un problema con la base de datos.",
  [AppErrorCode.DATABASE_CONNECTION_ERROR]:
    "No pudimos conectar con la base de datos",
  [AppErrorCode.CONFIGURATION_ERROR]:
    "Hay un problema de configuración. Contactá al soporte",
  [AppErrorCode.INTERNAL_SERVER_ERROR]: "Ocurrió un error inesperado.",
  [AppErrorCode.TIMEOUT_ERROR]: "La operación demoró demasiado tiempo.",
  [AppErrorCode.UNKNOWN_ERROR]: "Error código 3312",

  // Red y Conectividad
  [AppErrorCode.NETWORK_ERROR]: "Verificá tu conexión a internet",
  [AppErrorCode.CONNECTION_TIMEOUT]:
    "La conexión demoró demasiado. Verificá tu internet",
  [AppErrorCode.SERVICE_UNAVAILABLE]:
    "El servicio no está disponible en este momento",

  // Concurrencia
  [AppErrorCode.RESOURCE_LOCKED]:
    "Este registro está siendo usado por otro usuario.",
  [AppErrorCode.CONCURRENT_MODIFICATION]:
    "Otro usuario modificó esta información.",
  [AppErrorCode.TRANSACTION_FAILED]: "No se pudo completar la operación.",
  [AppErrorCode.USER_EMAIL_ALREADY_INVITED]:
    "Ya existe un usuario con ese email. Revisá la lista de usuarios: puede " +
    "estar dado de baja, o tener una invitación pendiente de aceptar.",
  // La invitación SÍ salió (identity), y lo que falló es el alta del empleado
  // en core. Cuando acepta, aparece en la lista —el BFF lo arma desde
  // identity— pero sin sus datos de core. Reintentar choca con el 409.
  [AppErrorCode.OPERATIONAL_NOT_PERSISTED]:
    "Se envió la invitación por mail, pero no se pudieron guardar sus datos " +
    "de empleado (comisión, sueldo y locales). Cuando acepte la invitación va " +
    "a aparecer en la lista, pero sin esos datos. No lo vuelvas a crear: " +
    "avisá a soporte para completarlo.",
  [AppErrorCode.MEMBER_ALREADY_EXISTS]: "Ya está en tu equipo.",
  [AppErrorCode.GRANT_RESOURCE_NOT_ENABLED]:
    "Tu negocio no tiene habilitados algunos de esos permisos.",
};

/**
 * Clase para mapear errores del backend a mensajes user-friendly en español
 */
export class ErrorMapper {
  /**
   * Obtiene un mensaje user-friendly para un código de error
   * @param errorCode Código de error del backend
   * @returns Mensaje en español para mostrar al usuario
   */
  static getMessage(errorCode: string): string {
    // Validar que errorCode no sea undefined, null o vacío
    if (!errorCode || typeof errorCode !== "string") {
      return "Error código 3312";
    }

    // Intentar encontrar el código de error en el mapeo
    if (errorCode in ERROR_MESSAGES) {
      return ERROR_MESSAGES[errorCode as AppErrorCode];
    }

    // Mapeo de compatibilidad con códigos legacy
    const legacyMappings: Record<string, string> = {
      E001: "Ya existe un elemento con este nombre",
    };

    if (errorCode in legacyMappings) {
      return legacyMappings[errorCode];
    }

    // Mensaje por defecto
    return "Error código 3312";
  }

  /**
   * Procesa un error del backend y extrae el mensaje apropiado
   * @param error Error del backend o axios error
   * @returns Mensaje user-friendly para mostrar
   */
  static processError(error: any): string {
    // Si es un error de axios con respuesta del backend
    if (error?.response?.data) {
      const backendError = error.response.data as BackendError;
      if (backendError.errorCode) {
        return this.getMessage(backendError.errorCode);
      }
      if (backendError.message) {
        return backendError.message;
      }
    }

    // Si es un error directo con errorCode
    if (error?.errorCode) {
      return this.getMessage(error.errorCode);
    }

    // Si es un error directo con message
    if (error?.message) {
      return error.message;
    }

    // Si es un string directo
    if (typeof error === "string") {
      return error;
    }

    // Fallback
    return this.getMessage("UNKNOWN_ERROR");
  }
}

/**
 * Patrones que delatan un mensaje **técnico**: algo que le sirve a quien
 * programa y no le dice nada a quien vende.
 *
 * El caso que motivó la lista: apagar un ajuste contra un endpoint que todavía
 * no existía le mostró al usuario el toast
 * `Cannot PUT /api/v1/people/business/biz_019d2693-…/seller-on-documents`,
 * con el id del negocio adentro. Es el 404 de Nest, que viaja en
 * `response.data.message` — o sea, indistinguible de un mensaje de dominio si
 * uno sólo mira "¿vino un message?".
 */
const TECHNICAL_MESSAGE: RegExp[] = [
  // 404 de Nest/Express: siempre trae el método y la ruta.
  /^Cannot (GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)\b/i,
  // Los genéricos de axios.
  /^Request failed with status code/i,
  /^Network Error$/i,
  /^timeout of \d+ ?ms exceeded/i,
  /^ECONNABORTED/i,
  // El último recurso de `IpcRest.handleError` cuando no hay nada que mostrar.
  /^Unknown error$/i,
  // Una página de error entera (nginx, el index.html de vite).
  /<\/?[a-z][\s\S]*>/i,
  // Cualquier cosa que incluya una ruta de la API.
  /\/api\//,
];

/** ¿Este texto es presentable a alguien que está vendiendo? */
export const isTechnicalErrorMessage = (message: unknown): boolean => {
  if (typeof message !== "string" || message.trim() === "") return true;
  return TECHNICAL_MESSAGE.some((pattern) => pattern.test(message.trim()));
};

/**
 * El texto que se le muestra al usuario cuando una operación falla.
 *
 * El orden importa y es el punto de la función:
 *
 * 1. **`errorCode` del backend** → el mensaje en español de {@link ErrorMapper}.
 *    Es el camino bueno y el que ya existía, sólo que los toasts no lo usaban.
 * 2. **Un `message` del backend que NO parece técnico** → se muestra tal cual.
 *    "No hay stock suficiente" o "El email ya está registrado" le dicen al
 *    usuario qué hacer; perderlos por prudencia sería peor que el bug.
 * 3. **Cualquier otra cosa** → el genérico de quien llama.
 *
 * Antes esto era `error.message || errorMessage` en `useRestMutation`, así que
 * el genérico era sólo el fallback de un error sin texto: **el crudo del
 * backend ganaba siempre**. Contra lo que pide el `CLAUDE.md` de este repo, y
 * con la ruta y los ids a la vista del cliente.
 */
export const messageForUser = (error: any, fallback: string): string => {
  const errorCode = error?.response?.data?.errorCode ?? error?.errorCode;
  if (typeof errorCode === "string" && errorCode in ERROR_MESSAGES) {
    return ErrorMapper.getMessage(errorCode);
  }

  const backendMessage =
    error?.response?.data?.message ?? error?.response?.data?.error;
  if (!isTechnicalErrorMessage(backendMessage)) return backendMessage as string;

  // `error.message` es lo que `IpcRest.handleError` ya resolvió: puede ser el
  // mensaje del backend (bueno) o el genérico de axios (técnico).
  if (!isTechnicalErrorMessage(error?.message)) return error.message as string;

  return fallback;
};

export const errorMapper = new ErrorMapper();

/**
 * El texto de un error del backend, **siempre como string**.
 *
 * # Por qué existe
 *
 * `IpcRest.handleError` armaba el mensaje con
 * `data.message || data.error || error.message` y después le hacía
 * `msg.includes("UNAUTHORIZED")`. Eso asume que `data.error` es un string, y
 * el BFF **devuelve un objeto** en su formato estructurado:
 *
 * ```json
 * { "error": { "code": "NOT_FOUND", "context": "el business … no tiene
 *   suscripción", "message": "NOT_FOUND", "traceId": "…" } }
 * ```
 *
 * Con ese cuerpo, `msg` quedaba siendo el objeto y `msg.includes` tiraba
 * `TypeError: msg.includes is not a function` **adentro del propio manejador de
 * errores**. El `TypeError` reemplazaba al error real y se llevaba puestos el
 * `status` y el `response`, así que quien llamaba no podía distinguir NADA: ni
 * un 404 de dominio, ni un 500, ni una caída de red. Reproducido el 2026-09-12
 * en `GET /api/v1/business/billing`.
 *
 * # El orden
 *
 * Se prefiere lo más legible para una persona: `context` explica qué pasó,
 * `code`/`message` son etiquetas. Nunca devuelve un objeto.
 */
export const backendErrorText = (error: any): string => {
  const data = error?.response?.data;
  const candidatos = [
    data?.message,
    typeof data?.error === "string" ? data.error : undefined,
    data?.error?.context,
    data?.error?.message,
    data?.error?.code,
    error?.message,
  ];

  const texto = candidatos.find(
    (c) => typeof c === "string" && c.trim() !== ""
  );

  return texto ?? "Unknown error";
};
